import { useMemo, useState } from "react";
import { fmtEuro } from "../../../utils/currency";
import { App, Table, Select, Spin, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  ReloadOutlined,
  PrinterOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import BackButton from "../../../components/BackButton";
import ProfileDropdown from "../../../components/ProfileDropdown";
import {
  useFiscalIssues,
  useCloseBillRetry,
  useStornoRetry,
} from "../../../hooks/useBills";
import { usePrinterNotifications } from "../../../hooks/usePrinterNotifications";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { logout, setAccess } from "../../../store/authSlice";
import { denyAccess } from "../../../api/access";
import type {
  FiscalIssue,
  FiscalResult,
  StornoIssue,
} from "../../../api/bills";

const R = {
  bg: "var(--color-canvas)",
  surface: "var(--color-surface)",
  border: "var(--color-shade-2)",
  text: "var(--color-ink)",
  textSoft: "var(--color-body)",
  textMuted: "var(--color-muted)",
  ink: "var(--color-ink)",
  brand: "var(--color-brand-ink)",
  brandDeep: "var(--color-brand-deep-ink)",
  brandTint: "var(--color-brand-tint)",
  mono: '"JetBrains Mono", ui-monospace, monospace',
};

// Тонални тинтове за статус на фискализацията (§ semantic tonal tints):
// failed = категоричен отказ от принтера; timeout/pending = неизвестен изход.
const STATUS_TINTS: Record<
  FiscalIssue["fiscal_status"],
  { tint: string; ink: string }
> = {
  failed: { tint: "var(--color-danger-tint)", ink: "var(--color-danger-ink)" },
  timeout: { tint: "var(--color-fill)", ink: "var(--color-brand-deep-ink)" },
  pending: { tint: "var(--color-fill)", ink: "var(--color-body)" },
};

// Страницата живее в модул „Мониторинг" (/monitoring/fiscal-issues) —
// самостоятелна е, без sidebar, затова хедърът носи BackButton, не хамбургер.
type Props = { isNarrow?: boolean };

export const FiscalIssuesReport = ({ isNarrow = false }: Props = {}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // App.useApp() вместо статичното antd `message` — под React 19 статичните
  // message.* изобщо не рендират (вж. CLAUDE.md), изходът от Retry беше невидим.
  const { message, modal } = App.useApp();
  const { notifyPrinterError, notifyPrinterTimeout } = usePrinterNotifications();
  const access = useAppSelector((s) => s.auth.access);
  const names = useAppSelector((s) => s.auth.names);
  const username = useAppSelector((s) => s.auth.username);
  const displayName = names || username || "";

  const [days, setDays] = useState(7);
  const [retryingId, setRetryingId] = useState<number | null>(null);

  const { data, isLoading, isFetching, refetch } = useFiscalIssues(days);
  const issues = useMemo(() => data?.issues ?? [], [data]);
  const stornoIssues = useMemo(() => data?.storno_issues ?? [], [data]);
  const retryMutation = useCloseBillRetry();
  const stornoRetryMutation = useStornoRetry();

  // Плочките броят и двата списъка: „проблем" е всеки документ, който не е
  // потвърден на хартия — бон ИЛИ кредитно известие.
  const failedCount = useMemo(
    () =>
      [...issues, ...stornoIssues].filter((i) => i.fiscal_status === "failed")
        .length,
    [issues, stornoIssues],
  );
  const totalCount = issues.length + stornoIssues.length;
  const unknownCount = totalCount - failedCount;

  // Общият шаблон на двата retry-я: покажи изхода, опресни списъка. Какво
  // точно се повтаря (бон или сторно) решава подаденият mutate.
  const runRetry = async (
    billId: number,
    mutate: () => Promise<{ fiscal_result?: FiscalResult }>,
    successText: string,
  ) => {
    setRetryingId(billId);
    try {
      const result = await mutate();
      const fiscal = result.fiscal_result;
      if (!fiscal || fiscal.status === "success") {
        message.success(successText);
      } else if (fiscal.status === "timeout") {
        notifyPrinterTimeout(
          fiscal.error || t("reportsPage.fiscalIssuesRetryTimeout"),
        );
      } else {
        notifyPrinterError(
          fiscal.error || t("reportsPage.fiscalIssuesRetryError"),
        );
      }
    } catch (e: unknown) {
      const detail =
        typeof e === "object" && e !== null && "response" in e
          ? (e as { response?: { data?: { detail?: string } } }).response?.data
              ?.detail
          : null;
      notifyPrinterError(detail || t("reportsPage.fiscalIssuesRetryError"));
    } finally {
      setRetryingId(null);
      refetch();
    }
  };

  // „Неизвестен“/„Изчаква“: документът може вече да е излязъл, а повторението
  // отива на принтера на ТАЗИ сесия (BE `find_printer_for_user`), не на апарата
  // от първия опит — дедупът по request_id пази само на същата каса. Затова
  // въпрос преди повторението, не забрана; „Отказан“ (бон не е излизал) — направо.
  const confirmUnknownOutcome = (issue: FiscalIssue, retry: () => void) => {
    if (issue.fiscal_status === "failed") {
      retry();
      return;
    }
    modal.confirm({
      title: t("reportsPage.fiscalIssuesRetryUnknownTitle"),
      content: t("reportsPage.fiscalIssuesRetryUnknownBody"),
      icon: (
        <ExclamationCircleFilled
          style={{ color: "var(--color-brand-deep-ink)" }}
        />
      ),
      centered: true,
      okText: t("reportsPage.fiscalIssuesRetry"),
      cancelText: t("common.cancel"),
      onOk: retry,
    });
  };

  const handleRetry = (issue: FiscalIssue) =>
    // Без метод — справката повтаря същия документ със записания метод.
    confirmUnknownOutcome(issue, () =>
      runRetry(
        issue.bill_id,
        () => retryMutation.mutateAsync({ billId: issue.bill_id }),
        t("reportsPage.fiscalIssuesRetrySuccess"),
      ),
    );

  const handleStornoRetry = (issue: StornoIssue) =>
    confirmUnknownOutcome(issue, () =>
      runRetry(
        issue.bill_id,
        () => stornoRetryMutation.mutateAsync({ billId: issue.bill_id }),
        t("reportsPage.fiscalIssuesStornoRetrySuccess"),
      ),
    );

  const handleLogout = async () => {
    if (access) {
      try {
        await denyAccess();
      } catch (e) {
        console.error("Deny access error:", e);
      }
      dispatch(setAccess(false));
      navigate("/", { replace: true });
    } else {
      await dispatch(logout());
      navigate("/login", { replace: true });
    }
  };

  const fmtDateTime = (iso: string | null) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString(i18n.language, {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Общите колони на двете таблици (бонове и сторно). Actions колоната се
  // строи отделно — retry пътищата са различни и НЕ бива да се смесват:
  // сторно през /close/retry би издало ВТОРИ бон на вече платена сметка.
  // Генерична функция, не масив: antd колоните са инвариантни по типа на
  // реда и един масив не може да обслужи двете таблици.
  const buildSharedColumns = <T extends FiscalIssue>(): ColumnsType<T> => [
    {
      title: t("reportsPage.table"),
      dataIndex: "bill_name",
      key: "bill_name",
      width: 90,
      render: (name: string) => (
        <span style={{ fontWeight: 700, color: R.ink }}>{name}</span>
      ),
    },
    {
      title: t("reportsPage.fiscalIssuesClosedAt"),
      dataIndex: "closed_at",
      key: "closed_at",
      width: 130,
      sorter: (a, b) =>
        new Date(a.closed_at || 0).getTime() -
        new Date(b.closed_at || 0).getTime(),
      render: (v: string | null) => (
        <span style={{ fontFamily: R.mono, fontSize: 11.5 }}>
          {fmtDateTime(v)}
        </span>
      ),
    },
    {
      title: t("reportsPage.amount"),
      dataIndex: "total_amount",
      key: "total_amount",
      width: 100,
      align: "right",
      render: (v: string) => (
        <span
          style={{
            fontWeight: 700,
            color: R.ink,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {fmtEuro(v)}
        </span>
      ),
    },
    {
      title: t("reportsPage.waiter"),
      dataIndex: "waiter_name",
      key: "waiter_name",
      width: 150,
      responsive: ["md"],
      render: (v: string | null) => v || "—",
    },
    {
      title: t("reportsPage.paymentMethod"),
      dataIndex: "payment_method_name",
      key: "payment_method_name",
      width: 120,
      responsive: ["lg"],
      render: (v: string | null) => v || "—",
    },
    {
      title: t("reportsPage.fiscalIssuesStatus"),
      dataIndex: "fiscal_status",
      key: "fiscal_status",
      width: 130,
      filters: (["failed", "timeout", "pending"] as const).map((s) => ({
        text: t(`reportsPage.fiscalStatus_${s}`),
        value: s,
      })),
      onFilter: (value, rec) => rec.fiscal_status === value,
      render: (s: FiscalIssue["fiscal_status"], rec) => {
        const c = STATUS_TINTS[s];
        return (
          <Tooltip
            title={
              s === "failed"
                ? t("reportsPage.fiscalStatusFailedHint")
                : t("reportsPage.fiscalStatusUnknownHint")
            }
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "3px 10px",
                borderRadius: 999,
                background: c.tint,
                color: c.ink,
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {t(`reportsPage.fiscalStatus_${s}`)}
              {rec.attempts > 1 && (
                <span style={{ fontFamily: R.mono, fontWeight: 500 }}>
                  ×{rec.attempts}
                </span>
              )}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: t("reportsPage.fiscalIssuesError"),
      dataIndex: "fiscal_error",
      key: "fiscal_error",
      ellipsis: true,
      render: (v: string | null, rec) => (
        <Tooltip title={v}>
          <span style={{ color: R.textSoft }}>
            {rec.card && (
              <span
                style={{
                  display: "block",
                  color: "var(--color-brand-deep-ink)",
                  fontWeight: 700,
                }}
              >
                {t("reportsPage.fiscalCardPending")}
                {rec.card.rrn ? ` · RRN ${rec.card.rrn}` : ""} ·{" "}
                {Number(rec.card.amount).toFixed(2)}
              </span>
            )}
            {v || "—"}
          </span>
        </Tooltip>
      ),
    },
  ];

  // Фискален документ се издава само в деня на продажбата: за стар ред бутонът
  // липсва, а BE-то отказва retry с 400 (същото правило от двете страни).
  // Бонът се гейтва по closed_at, сторното — по последния опит.
  const isFromToday = (isoDate: string | null) =>
    isoDate !== null &&
    new Date(isoDate).toDateString() === new Date().toDateString();

  const actionsColumn = <T extends FiscalIssue>(
    onRetry: (rec: T) => void,
    getIssueDate: (rec: T) => string | null,
  ): ColumnsType<T>[number] => ({
    title: "",
    key: "actions",
    width: 150,
    align: "right",
    render: (_, rec) => {
      if (!isFromToday(getIssueDate(rec))) {
        return <span className="text-[12px] text-muted">—</span>;
      }
      const busy = retryingId === rec.bill_id;
      const idle = !busy && retryingId === null;
      return (
        <button
          onClick={() => onRetry(rec)}
          disabled={!idle}
          className={`h-8 px-3.5 rounded-lg border-none text-[12px] font-semibold inline-flex items-center gap-1.5 whitespace-nowrap transition-colors duration-100 ${
            busy
              ? "bg-brand-tint text-brand-deep-ink cursor-default"
              : idle
                ? "bg-brand text-white cursor-pointer hover:bg-brand-deep"
                : "bg-brand text-white cursor-default opacity-50"
          }`}
        >
          {busy ? (
            <SyncOutlined spin style={{ fontSize: 12 }} />
          ) : (
            <PrinterOutlined style={{ fontSize: 12 }} />
          )}
          {t("reportsPage.fiscalIssuesRetry")}
        </button>
      );
    },
  });

  const columns: ColumnsType<FiscalIssue> = [
    ...buildSharedColumns<FiscalIssue>(),
    actionsColumn(handleRetry, (rec) => rec.closed_at),
  ];

  // Сторно таблицата показва и номера на бона, който е трябвало да се
  // анулира — по него операторът намира хартиения документ.
  const stornoSharedColumns = buildSharedColumns<StornoIssue>();
  const stornoColumns: ColumnsType<StornoIssue> = [
    stornoSharedColumns[0],
    {
      title: t("reportsPage.fiscalIssuesReceiptNo"),
      dataIndex: "receipt_number",
      key: "receipt_number",
      width: 110,
      render: (v: string | null) => (
        <span className="font-pos-mono text-[11.5px]">{v || "—"}</span>
      ),
    },
    ...stornoSharedColumns.slice(1),
    actionsColumn<StornoIssue>(
      handleStornoRetry,
      (rec) => rec.last_attempt_at ?? rec.closed_at,
    ),
  ];

  const statTiles = [
    {
      label: t("reportsPage.fiscalIssuesTotal"),
      value: totalCount,
      Icon: PrinterOutlined,
      tint: R.brandTint,
      ink: R.brandDeep,
    },
    {
      label: t("reportsPage.fiscalIssuesFailed"),
      value: failedCount,
      Icon: WarningOutlined,
      tint: STATUS_TINTS.failed.tint,
      ink: STATUS_TINTS.failed.ink,
    },
    {
      label: t("reportsPage.fiscalIssuesUnknown"),
      value: unknownCount,
      Icon: ClockCircleOutlined,
      tint: STATUS_TINTS.timeout.tint,
      ink: STATUS_TINTS.timeout.ink,
    },
  ];

  return (
    <div className="h-full flex flex-col" style={{ background: R.bg }}>
      <style>{`
        @keyframes rspin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        .fi-scope .ant-select-selector {
          border-radius: 10px !important;
          border-color: ${R.border} !important;
          min-height: 36px !important;
          background: ${R.surface} !important;
        }
        .fi-scope .ant-select-focused .ant-select-selector,
        .fi-scope .ant-select-selector:hover {
          border-color: var(--color-brand) !important;
          box-shadow: 0 0 0 3px rgba(217,119,6,0.12) !important;
        }
        .fi-scope .ant-table { background: transparent; }
        .fi-scope .ant-table-thead > tr > th {
          background: ${R.bg} !important;
          color: ${R.textMuted} !important;
          font-weight: 700 !important;
          font-size: 10.5px !important;
          letter-spacing: 0.8px !important;
          text-transform: uppercase !important;
          border-bottom: 1px solid ${R.border} !important;
          padding: 12px 14px !important;
        }
        .fi-scope .ant-table-thead > tr > th::before { display: none !important; }
        .fi-scope .ant-table-measure-row { display: none !important; }
        .fi-scope .ant-table-tbody > tr > td {
          border-bottom: 1px solid ${R.border} !important;
          padding: 12px 14px !important;
          font-size: 12.5px;
          color: ${R.text};
          transition: background 120ms;
        }
        .fi-scope .ant-table-tbody > tr:hover > td { background: ${R.brandTint} !important; }
        .fi-scope .ant-table-tbody > tr:last-child > td { border-bottom: none !important; }
        .fi-scope .ant-pagination {
          padding: 14px 16px !important;
          margin: 0 !important;
          border-top: 1px solid ${R.border};
        }
        .fi-scope .ant-pagination .ant-pagination-item-active {
          background: #D97706 !important; border-color: #D97706 !important;
        }
        .fi-scope .ant-pagination .ant-pagination-item-active a { color: #fff !important; }
      `}</style>

      {/* Top bar */}
      <div
        className="flex items-center justify-between flex-shrink-0"
        style={{
          padding: isNarrow ? "10px 16px" : "11px 28px",
          minHeight: isNarrow ? 48 : 60,
          borderBottom: `1px solid ${R.border}`,
          background: access
            ? `radial-gradient(ellipse 40% 120% at 100% 50%, ${R.brandTint} 0%, transparent 70%), ${R.surface}`
            : R.surface,
          gap: 10,
        }}
      >
        <div
          className="flex items-center"
          style={{ gap: 10, minWidth: 0, flex: 1 }}
        >
          <BackButton onClick={() => navigate("/monitoring")} />
          <img
            src="/logo-ts.png"
            alt="UnrealSoft"
            className={`shrink-0 select-none object-contain ${
              isNarrow ? "h-[26px] w-[26px]" : "h-[34px] w-[34px]"
            }`}
          />
          <div className="min-w-0 flex-1 leading-none">
            <h1
              className={`m-0 overflow-hidden font-bold leading-[1.2] tracking-[-0.3px] text-ellipsis whitespace-nowrap text-ink ${
                isNarrow ? "text-[16px]" : "text-[18px]"
              }`}
            >
              {t("reportsPage.fiscalIssuesTitle")}
            </h1>
            {!isNarrow && (
              <div className="mt-[4px] text-[11px] leading-none tracking-[0.3px] text-muted">
                {t("reportsPage.fiscalIssuesSubtitle")}
              </div>
            )}
          </div>
        </div>

        <div
          className="flex items-center"
          style={{ gap: isNarrow ? 4 : 10, flexShrink: 0 }}
        >
          <button
            onClick={() => refetch()}
            title={t("reportsPage.refresh")}
            style={{
              height: 30,
              width: 30,
              borderRadius: 6,
              background: "transparent",
              border: "none",
              color: R.textMuted,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "background 120ms, color 120ms",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--color-shade-1)";
              e.currentTarget.style.color = R.ink;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = R.textMuted;
            }}
          >
            <ReloadOutlined
              style={{
                fontSize: 14,
                animation: isFetching
                  ? "rspin 900ms cubic-bezier(.5,0,.5,1) infinite"
                  : "none",
              }}
            />
          </button>
          {!isNarrow && (
            <span
              style={{
                width: 1,
                height: 20,
                background: R.border,
                margin: "0 4px",
              }}
            />
          )}
          <ProfileDropdown
            displayName={displayName}
            username={username ?? ""}
            access={access}
            isNarrow={isNarrow}
            onLogout={handleLogout}
          />
        </div>
      </div>

      {/* Main scroll */}
      <div
        className="fi-scope flex-1 overflow-y-auto"
        style={{ padding: isNarrow ? "14px 14px 24px" : "22px 28px 32px" }}
      >
        {/* Stat tiles */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isNarrow ? "1fr" : "repeat(3, 1fr)",
            gap: isNarrow ? 10 : 14,
            marginBottom: isNarrow ? 14 : 18,
          }}
        >
          {statTiles.map((tile) => (
            <div
              key={tile.label}
              style={{
                padding: isNarrow ? "16px 14px" : "18px 20px",
                background: R.surface,
                border: `1px solid ${R.border}`,
                borderRadius: 14,
                display: "flex",
                alignItems: "center",
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: tile.tint,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <tile.Icon style={{ fontSize: 17, color: tile.ink }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: 1.2,
                    textTransform: "uppercase",
                    color: R.textMuted,
                  }}
                >
                  {tile.label}
                </div>
                <div
                  style={{
                    fontSize: 26,
                    fontWeight: 700,
                    letterSpacing: -1,
                    color: R.ink,
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1.15,
                  }}
                >
                  {tile.value}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* List card */}
        <div
          style={{
            background: R.surface,
            border: `1px solid ${R.border}`,
            borderRadius: 14,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: isNarrow ? "column" : "row",
              alignItems: isNarrow ? "stretch" : "center",
              justifyContent: "space-between",
              gap: 10,
              padding: isNarrow ? "14px 14px 0" : "18px 20px 0",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  letterSpacing: -0.3,
                  color: R.ink,
                }}
              >
                {t("reportsPage.fiscalIssuesListTitle")}
              </div>
              <div
                style={{ fontSize: 12, color: R.textMuted, marginTop: 2 }}
              >
                {t("reportsPage.fiscalIssuesListSubtitle")}
              </div>
            </div>
            <Select
              value={days}
              onChange={setDays}
              style={{ width: isNarrow ? "100%" : 170 }}
              options={[
                { value: 1, label: t("reportsPage.fiscalIssuesDays1") },
                { value: 7, label: t("reportsPage.fiscalIssuesDays7") },
                { value: 30, label: t("reportsPage.fiscalIssuesDays30") },
              ]}
            />
          </div>

          {isLoading ? (
            <div
              style={{
                padding: 60,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Spin />
            </div>
          ) : issues.length === 0 ? (
            <div
              style={{
                padding: "48px 20px",
                textAlign: "center",
                color: R.textMuted,
                fontSize: 13,
              }}
            >
              {t("reportsPage.fiscalIssuesEmpty")}
            </div>
          ) : (
            <div style={{ marginTop: 12 }}>
              <Table<FiscalIssue>
                columns={columns}
                dataSource={issues}
                rowKey="bill_id"
                pagination={{ pageSize: 20, hideOnSinglePage: true }}
                scroll={{ x: 720 }}
                size="middle"
              />
            </div>
          )}
        </div>

        {/* Сторно карта: бонът Е излязъл, кредитното известие към него не е
            потвърдено. Отделна от горната, защото и retry пътят е отделен
            (/storno/retry). Не се показва празна — нормалното ѝ състояние е
            „няма такива редове". */}
        {stornoIssues.length > 0 && (
          <div
            className={`bg-surface border border-shade-2 rounded-[14px] overflow-hidden ${
              isNarrow ? "mt-[14px]" : "mt-[18px]"
            }`}
          >
            <div className={isNarrow ? "px-[14px] pt-[14px]" : "px-5 pt-[18px]"}>
              <div className="text-[15px] font-bold tracking-[-0.3px] text-ink">
                {t("reportsPage.fiscalIssuesStornoListTitle")}
              </div>
              <div className="text-[12px] text-muted mt-0.5">
                {t("reportsPage.fiscalIssuesStornoListSubtitle")}
              </div>
            </div>
            <div className="mt-3">
              <Table<StornoIssue>
                columns={stornoColumns}
                dataSource={stornoIssues}
                rowKey="bill_id"
                pagination={{ pageSize: 20, hideOnSinglePage: true }}
                scroll={{ x: 820 }}
                size="middle"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
